import { useState } from 'react';
import { Alert, Anchor, Button, PasswordInput, Text, TextInput } from '@mantine/core';
import { Link } from 'react-router-dom';

import { MIN_PASSWORD, register } from '../lib/auth';
import { AuthCard } from './SignInPage';

/**
 * Creating an account, which is not the same as being signed in.
 *
 * Registration ends on a page that says "look in your inbox" rather than in
 * the app, because the address is unconfirmed until the link in it is
 * followed, and an account that cannot be signed into would otherwise look
 * like a failure.
 */
export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await register(email, password);
      setDone(true);
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthCard title="Almost there">
        <Text size="sm">
          A link is on its way to {email}. Once you have followed it you can sign in, and found a
          workspace or be seated in one.
        </Text>
        <Anchor component={Link} to="/" size="sm">
          To sign-in
        </Anchor>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Create an account" subtitle="One account per person; a workspace is shared by seating.">
      <TextInput
        label="Email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <PasswordInput
        label="Password"
        description={`At least ${MIN_PASSWORD} characters.`}
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && password.length >= MIN_PASSWORD) void submit();
        }}
      />
      {error ? (
        <Alert color="red" variant="light">
          {error.message}
        </Alert>
      ) : null}
      <Button
        loading={busy}
        disabled={!email.trim() || password.length < MIN_PASSWORD}
        onClick={() => void submit()}
      >
        Create the account
      </Button>
      <Anchor component={Link} to="/" size="sm">
        I already have an account
      </Anchor>
    </AuthCard>
  );
}
